import { Box } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import { tokens } from '@synergy/theme-palo';

/**
 * What the unit beside the figure takes up, heading and cell alike. Enough for
 * "Packung" beside "25.000"; a longer unit ends in an ellipsis. Narrower than
 * before so a position table fits at 1568 pixels (R-179).
 */
export const PALO_UNIT_WIDTH = 46;
const FIGURE_WIDTH = 60;
const GAP = 4;

/**
 * The heading of a quantity column sits over the figures, not over the units:
 * it steps back by the unit's own column and the gap before it.
 */
export const PALO_QUANTITY_HEAD_SX = { paddingRight: `${PALO_UNIT_WIDTH + GAP}px` } as const;

export interface PaloQuantityCellProps {
    /** "Stück", "Packung" — left out where a row has no unit of its own. */
    unit?: ReactNode;
    children: ReactNode;
}

/**
 * A quantity and its unit, in two fixed columns.
 *
 * Both used to sit in one right-aligned run, so a row of "Packung" pushed its
 * figure further left than a row of "Stück" and the column read as ragged.
 * The unit keeps a place of its own now, wide enough for the usual ones, and
 * the figures line up under the heading (#2208).
 */
export const PaloQuantityCell = ({ unit, children }: PaloQuantityCellProps): ReactElement => (
    // Centred, with the unit holding its own line height: the eye measures the
    // unit against the bordered field beside it, not against the baseline of
    // the digits, so aligning the baselines left it reading as superscript. The
    // tight line height is what makes centring land on the glyphs rather than
    // on the leading the unit inherited (#2216).
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: `${GAP}px` }}>
        {/* minWidth 0 against PoolTextfield's own 90px minimum: the field used to
            stand 4px wider than its column and ate most of the gap, which read as
            "1Stück" (#2216). */}
        <Box sx={{ width: `${FIGURE_WIDTH}px`, '& .MuiFormControl-root': { width: '100%', minWidth: 0 } }}>
            {children}
        </Box>
        <Box
            sx={{
                width: `${PALO_UNIT_WIDTH}px`,
                fontSize: '11.5px',
                color: tokens.color.ink2,
                textAlign: 'left',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {unit}
        </Box>
    </Box>
);
